import { Injectable } from '@angular/core';
import { GenerateAgreementsFullRequestParams, GenerateAgreementsPartialRequestParams, StoreAgreementAcknowledgmentsRequestParams } from '@nationwide/dgs-angular-billing-common';
import { BankAccountPaymentItem } from '../../../billing/payment-preferences/shared/models/bankaccount-item.model';
import { BankCardPaymentItem } from '../../../billing/payment-preferences/shared/models/bankcard-item.model';
import { SessionService } from '../../session/session.service';
import { environment } from '../../../../environments/environment';
import { PaymentItem } from '../../../billing/payment-preferences/shared/models/payment-item.model';
import { DatePipe } from '@angular/common';
import { PaymentInterface } from '../../../billing/payment-preferences/shared/models/payment-interface.model';

const last4Length = -4;

@Injectable({
    providedIn: 'root'
})
export class NuxeoService {
    constructor(
        private session: SessionService,
        private datePipe: DatePipe
    ) { }

    buildGenerateAgreementsPartialPayload(paymentMethod: PaymentItem): GenerateAgreementsPartialRequestParams {
        if (paymentMethod.isApplePay) {
            return {
                ...this.getBaseParams(),
                payload: {
                    ...this.getCommonBasePayload(),
                    firstName: this.session.firstName,
                    lastName: this.session.lastName,
                    paymentType: 'oneTimePayment',
                    templateId: 'ApplePay_Partial'
                }
            };
        }

        const displayNumbers = (<PaymentInterface> paymentMethod.item).displayNumbers;
        const isBankCard = !!(<BankCardPaymentItem> paymentMethod.item).maskedCardNumber;
        const generateAgreementsParams: GenerateAgreementsPartialRequestParams = {
            ...this.getBaseParams(),
            payload: {
                ...this.getCommonBasePayload(),
                paymentType: 'oneTimePayment',
                paymentMethodLast4Digits: displayNumbers?.replace(/\D/g, '')?.slice(last4Length),
                templateId: isBankCard ? 'BankCard_Partial' : 'BankAccount_Partial'
            }
        };

        return generateAgreementsParams;
    }

    buildGenerateAgreementsFullPayload(paymentMethod: PaymentItem, isOneTimePayment: boolean, isQuickPay = false, paymentDetails?: PaymentDetails): GenerateAgreementsFullRequestParams {
        let generateAgreementsParams: GenerateAgreementsFullRequestParams;

        let basePayload = {
            ...this.getCommonBasePayload(),
            ecn: this.session.ecn,
            billingAccountNumber: '',
            policyNumber: '',
            state: environment.ENTERPRISE_LEGAL_AGREEMENTS.NATIONWIDE_DETAILS.state,
            zipcode: environment.ENTERPRISE_LEGAL_AGREEMENTS.NATIONWIDE_DETAILS.zip
        };

        basePayload = {
            ...basePayload,
            ...this.addSharedBasePayloadForFullDocument(isOneTimePayment, paymentDetails)
        };

        if (paymentMethod.isBankAccount) {
            basePayload = {
                ...basePayload,
                ...this.addBUToBasePayload(isOneTimePayment)
            };
            const bankAccountPaymentMethod = <BankAccountPaymentItem> paymentMethod.item;

            generateAgreementsParams = {
                ...this.getBaseParams(),
                payload: {
                    ...basePayload,
                    paymentMethodLast4Digits: bankAccountPaymentMethod.maskedBankAccountNumber?.replace(/\D/g, '')?.slice(last4Length),
                    templateId: 'BankAccount_Full',
                    firstName: this.getFirstNameForBankAccount(bankAccountPaymentMethod),
                    lastName: this.getLastNameForBankAccount(paymentMethod, isQuickPay)
                }
            };
        } else if (paymentMethod.isBankCard) {
            const bankCardPaymentMethod = <BankCardPaymentItem> paymentMethod.item;

            generateAgreementsParams = {
                ...this.getBaseParams(),
                payload: {
                    ...basePayload,
                    paymentMethodLast4Digits: bankCardPaymentMethod.maskedCardNumber?.replace(/\D/g, '')?.slice(last4Length),
                    templateId: 'BankCard_Full',
                    firstName: bankCardPaymentMethod.firstName,
                    lastName: isQuickPay ? paymentMethod.displayForMaskedLastName : bankCardPaymentMethod.lastName
                }
            };
        } else if (paymentMethod.isApplePay) {
            generateAgreementsParams = {
                ...this.getBaseParams(),
                payload: {
                    ...basePayload,
                    firstName: this.session.firstName,
                    lastName: this.session.lastName,
                    paymentType: 'oneTimePayment',
                    templateId: 'ApplePay_Full'
                }
            };
        }
        return generateAgreementsParams;
    }

    buildStoreAgreementAcknowledgementsPayload(paymentMethod: PaymentItem, isOneTimePayment: boolean, isQuickPay = false, paymentDetails?: PaymentDetails): StoreAgreementAcknowledgmentsRequestParams {
        let storeAgreementAcknowledgementsParams: StoreAgreementAcknowledgmentsRequestParams;

        let basePayload = {
            ...this.getCommonBasePayload(),
            state: environment.ENTERPRISE_LEGAL_AGREEMENTS.NATIONWIDE_DETAILS.state,
            zipcode: environment.ENTERPRISE_LEGAL_AGREEMENTS.NATIONWIDE_DETAILS.zip,
            ecn: this.session.ecn,
            billingAccountNumber: '',
            policyNumber: ''
        };

        basePayload = {
            ...basePayload,
            ...this.addSharedBasePayloadForFullDocument(isOneTimePayment, paymentDetails)
        };

        if (paymentMethod.isBankAccount) {
            basePayload = {
                ...basePayload,
                ...this.addBUToBasePayload(isOneTimePayment)
            };
            const bankAccountPaymentMethod = <BankAccountPaymentItem> paymentMethod.item;

            storeAgreementAcknowledgementsParams = {
                ...this.getBaseParams(),
                payload: {
                    ...basePayload,
                    paymentMethodLast4Digits: bankAccountPaymentMethod.maskedBankAccountNumber?.replace(/\D/g, '')?.slice(last4Length),
                    templateId: 'BankAccount_Full',
                    firstName: this.getFirstNameForBankAccount(bankAccountPaymentMethod),
                    lastName: this.getLastNameForBankAccount(paymentMethod, isQuickPay)
                }
            };
        } else if (paymentMethod.isBankCard) {
            const bankCardPaymentMethod = <BankCardPaymentItem> paymentMethod.item;

            storeAgreementAcknowledgementsParams = {
                ...this.getBaseParams(),
                payload: {
                    ...basePayload,
                    paymentMethodLast4Digits: bankCardPaymentMethod.maskedCardNumber?.replace(/\D/g, '')?.slice(last4Length),
                    templateId: 'BankCard_Full',
                    firstName: bankCardPaymentMethod.firstName,
                    lastName: isQuickPay ? paymentMethod.displayForMaskedLastName : bankCardPaymentMethod.lastName
                }
            };
        } else if (paymentMethod.isApplePay) {
            storeAgreementAcknowledgementsParams = {
                ...this.getBaseParams(),
                payload: {
                    ...basePayload,
                    firstName: this.session.firstName || '***',
                    lastName: this.session.lastName || '***',
                    templateId: 'ApplePay_Full'
                }

            };
        }
        return storeAgreementAcknowledgementsParams;
    }

    private getCommonBasePayload(): any {
        return {
            address: environment.ENTERPRISE_LEGAL_AGREEMENTS.NATIONWIDE_DETAILS.address,
            phoneNumber: environment.ENTERPRISE_LEGAL_AGREEMENTS.NATIONWIDE_DETAILS.phoneNumber,
            timeZone: environment.ENTERPRISE_LEGAL_AGREEMENTS.NATIONWIDE_DETAILS.timeZone,
            webAddress: environment.ENTERPRISE_LEGAL_AGREEMENTS.NATIONWIDE_DETAILS.webAddress
        };
    }

    private addSharedBasePayloadForFullDocument(isOneTimePayment: boolean, paymentDetails?: PaymentDetails): any {
        const payload: any = {};

        if (isOneTimePayment) {
            if (paymentDetails?.isPolicyLevel) {
                payload.policyNumber = paymentDetails?.agreementToPostPaymentTo;
            } else {
                if (!paymentDetails?.isCyberLife) {
                    payload.billingAccountNumber = this.session.billingAccountNumber;
                }
                payload.policyNumber = this.session.policyNumbersRelatedToAccount?.join(', ');
            }

            payload.paymentDetails = {
                transactionAmount: paymentDetails?.paymentAmountString,
                transactionDate: this.datePipe.transform(paymentDetails.payDate, 'yyyy/MM/dd')
            };
            payload.paymentType = 'oneTimePayment';
        } else {
            payload.billingAccountNumber = this.session.billingAccountNumber;
            payload.policyNumber = this.session.policyNumbersRelatedToAccount?.join(', ');
            payload.paymentType = 'recurringPayment';
        }

        return payload;
    }

    private addBUToBasePayload(isOneTimePayment: boolean): any {
        const payload: any = {};
        if (!isOneTimePayment) {
            payload.buAmountLimit = environment.ENTERPRISE_LEGAL_AGREEMENTS.buAmountLimit;
        }

        return payload;
    }

    private getBaseParams(): any {
        return {
            accessToken: this.session.accessToken,
            messageId: `${Date.now()}`,
            consumerId: environment.ENTERPRISE_LEGAL_AGREEMENTS.consumerId
        };
    }

    private getFirstNameForBankAccount(paymentMethod: BankAccountPaymentItem): string {
        return paymentMethod.fullName?.split(' ')?.[0] || paymentMethod.firstName;
    }

    private getLastNameForBankAccount(paymentMethod: PaymentItem, isQuickPay: boolean): string {
        const lastName = isQuickPay ? paymentMethod.displayForMaskedLastName : (<BankAccountPaymentItem> paymentMethod.item).lastName;
        const fullName = (<BankAccountPaymentItem> paymentMethod.item).fullName;
        if (fullName) {
            const firstSpaceIndex = fullName?.indexOf(' ') ?? -1;
            const derivedLastName = firstSpaceIndex >= 0 ? fullName.substring(firstSpaceIndex + 1) : '***';
            return derivedLastName;
        }
        return lastName;
    }
}

export interface PaymentDetails {
    isCyberLife: boolean;
    isPolicyLevel: boolean;
    agreementToPostPaymentTo: string;
    paymentAmountString: string;
    payDate: string;
};